// Места для заголовка и действий экрана в верхней полосе телефона и планшета (задача D5).
//
// На ширине < 1024 сайдбара нет, и верхняя полоса — «☰ {название экрана} {пилюля} {действие}». Название и действия
// ставит туда сам экран (`PageShell`, через портал): `h1` один на экран, и после перехода фокус встаёт на него
// (route-focus). Пока экран полосу не занял (заглушка, «Нет доступа»), полоса пишет название раздела сама.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface TopbarSlotValue {
  /** Куда экран ставит заголовок; `null` — полосы нет (широкий экран) или она ещё не отрисована. */
  readonly titleSlot: HTMLElement | null;
  /** Куда экран ставит действия (справа от пилюли). */
  readonly actionsSlot: HTMLElement | null;
  /** Сколько экранов заняли полосу (0 — полоса пишет название раздела сама). */
  readonly claims: number;
  readonly setTitleSlot: (node: HTMLElement | null) => void;
  readonly setActionsSlot: (node: HTMLElement | null) => void;
  /** Экран занимает полосу; отдаёт «освободить». */
  readonly claim: () => () => void;
}

const TopbarSlotContext = createContext<TopbarSlotValue | null>(null);

export function TopbarSlotProvider({ children }: { readonly children: ReactNode }) {
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((n) => n + 1);
    return () => {
      setClaims((n) => n - 1);
    };
  }, []);
  const value = useMemo(
    () => ({ titleSlot, actionsSlot, claims, setTitleSlot, setActionsSlot, claim }),
    [titleSlot, actionsSlot, claims, claim],
  );
  return <TopbarSlotContext.Provider value={value}>{children}</TopbarSlotContext.Provider>;
}

/** Места в верхней полосе; вне каркаса (тесты компонентов) — `null`. */
export function useTopbarSlot(): TopbarSlotValue | null {
  return useContext(TopbarSlotContext);
}
